import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, Handshake, DollarSign, Info } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ICON = {
  new_client: Handshake,
  commission: DollarSign,
  default: Info,
};

function fmtWhen(iso) {
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60 * 1000) return "agora";
    if (diff < 3600 * 1000) return `há ${Math.floor(diff / 60000)}min`;
    if (diff < 86400 * 1000) return `há ${Math.floor(diff / 3600000)}h`;
    return d.toLocaleDateString("pt-BR");
  } catch { return ""; }
}

export default function NotificationsBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const load = () => {
    if (!user) return;
    setLoading(true);
    api.get("/me/notifications").then(({ data }) => {
      setItems(data.items || []);
      setUnread(data.unread || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    if (!user) return;
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!user) return null;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try {
        await api.post("/me/notifications/read");
        setUnread(0);
      } catch (e) {
        console.debug("notifications/read failed:", e?.message || e);
      }
    }
  };

  const go = (n) => {
    setOpen(false);
    if (n.kind === "new_client" || n.kind === "commission") navigate("/guia-comercial");
  };

  return (
    <div ref={containerRef} className="relative" data-testid="notifications-bell">
      <button onClick={toggle} aria-label="Notificações" data-testid="bell-toggle"
        className="relative w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span data-testid="bell-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-alert text-white text-[10px] font-bold px-1 flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="absolute right-0 mt-2 w-80 rounded-2xl border border-border bg-card shadow-xl z-50 overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b border-border">
              <p className="font-heading font-bold text-sm">Notificações</p>
              <button onClick={() => setOpen(false)} className="p-1 rounded-full hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {loading && <p className="text-xs text-muted-foreground text-center py-6">Carregando…</p>}
              {!loading && items.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-8 px-4">Nenhuma notificação ainda.</p>
              )}
              {items.map((n) => {
                const Icon = ICON[n.kind] || ICON.default;
                return (
                  <button key={n.id} data-testid={`notif-${n.id}`} onClick={() => go(n)}
                    className={`w-full text-left flex items-start gap-3 p-3 hover:bg-muted transition-colors ${!n.read ? "bg-primary/5" : ""}`}>
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-snug">{n.title}</p>
                      {n.body && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</p>}
                      <p className="text-[10px] text-muted-foreground mt-1">{fmtWhen(n.created_at)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
